<template>
    <div v-if="loaded" class="horse-edit-layout flex w-full gap-5 justify-content-start">
        <div class="menu flex flex-column h-full justify-content-between">
            <div class="flex flex-column align-items-end">
                <div v-for="(item,index) in filteredMenuItems" :key="index"
                     :class="`${isSelected(item) ? 'selected-menu-item ' : ''}menu-item`">
                    <router-link v-if="item.to" v-slot="{ href,navigate }"
                                 :to="{ name: item.to,params: { id: $route.params.id } }" custom>
                        <a @click="navigate">
                            {{ item.label }}
                        </a>
                    </router-link>
                </div>
            </div>
            <div class="flex flex-column gap-3 w-full p-3 menu-buttons">
                <CustomButton :label="$t('message.horseEdit.save')" secondary @click="requestValidationAndSave" :disabled="disableEdit"/>
                <CustomButton :label="$t('message.horseEdit.delete')" @click="showDeleteDialog" v-if="canDeleteHorse"/>
            </div>
            <Dialog v-model:visible="deleteDialogStatus" header="Delete horse?" modal :draggable="false"
                    :closable="true" style="max-width: 500px" >
                <span>
                    Are you sure you want to remove
                    <strong>{{ horse.name }}</strong>?
                </span>

                <template #footer>
                    <CustomButton :label="$t('message.horsePreview.cancel')" secondary @click="hideDeleteDialog"/>
                    <CustomButton :label="$t('message.horsePreview.delete')" @click="deleteHorse" autofocus/>
                </template>
            </Dialog>
        </div>
        <LibyanOrnament/>
        <div class="content">
            <router-view :horse="horse"
                         :editHorseHeader="editHorseHeader"
                         :showErrorMessages="showErrorMessages"
                         :validateRequest="validateRequest"
                         :validationErrors="validationErrors"
                         ref="routerView"
                         @updateHorse="updateHorseData"
                         @validationResult="handleValidationResult"
                         @save="handleSave"
                         @refreshHorse="getHorse"/>
        </div>

    </div>
</template>

<script>
import Button from "primevue/button";
import Toolbar from "primevue/toolbar";
import QrcodeVue from "qrcode.vue";
import Dialog from "primevue/dialog";
import CustomInputText from "../../../components/form/CustomInputText.vue";
import {
    editHorse, deleteHorse, getHorseEdit, setHorseAvatar,
} from "@/swagger/vue-api-client";
import LibyanOrnament from "@/components/LibyanOrnament.vue";
import HorseListSearchCriteria from "@/views/horse/horseList/components/HorseListSearchCriteria.vue";
import CustomButton from "@/components/form/CustomButton.vue";
import CustomIconButton from "@/components/form/CustomIconButton.vue";
import HorseTable from "@/views/horse/horseList/components/HorseTable.vue";
import HorsePreview from "@/views/horse/horseList/components/HorsePreview.vue";
import {SystemRole} from "@/utils/SystemRole";
import {HorseUtils} from "@/utils/HorseUtils";

export default {
    name: "HorseEditLayout",

    components: {
        Dialog,
        HorsePreview,
        HorseTable,
        CustomIconButton,
        CustomButton,
        HorseListSearchCriteria,
        LibyanOrnament,
        Button,
        Toolbar,
        CustomInputText,
        QrcodeVue,
    },

    setup() {
        // const confirm = useConfirm();
        // return { confirm };
    },

    data() {
        return {
            menuItems: [
                {
                    label: this.$t("message.horseEdit.main.menuItem"),
                    to: "horseEditMain",
                },
                {
                    label: this.$t("message.horseEdit.events.menuItem"),
                    to: "horseEditEvents",
                    roles: [SystemRole.ADMIN, SystemRole.FIRST_LEVEL_EDITOR, SystemRole.SECOND_LEVEL_EDITOR, SystemRole.THIRD_LEVEL_EDITOR, SystemRole.FOURTH_LEVEL_EDITOR],
                },
                {
                    label: this.$t("message.horseEdit.covering.menuItem"),
                    to: "horseEditCovering",
                    roles: [SystemRole.ADMIN, SystemRole.SECOND_LEVEL_EDITOR, SystemRole.THIRD_LEVEL_EDITOR],
                },
                {
                    label: this.$t("message.horseEdit.offspring.menuItem"),
                    to: "horseEditOffspring",
                },
                {
                    label: this.$t("message.horseEdit.laboratory.menuItem"),
                    to: "horseEditLaboratory",
                    roles: [SystemRole.ADMIN, SystemRole.THIRD_LEVEL_EDITOR],
                },
                {
                    label: this.$t("message.horseEdit.documents.menuItem"),
                    to: "horseEditDocuments",
                    roles: [SystemRole.ADMIN, SystemRole.SECOND_LEVEL_EDITOR, SystemRole.THIRD_LEVEL_EDITOR, SystemRole.FOURTH_LEVEL_EDITOR],
                },
                {
                    label: this.$t("message.horseEdit.history.menuItem"),
                    to: "horseEditHistory",
                    roles: [SystemRole.ADMIN, SystemRole.FOURTH_LEVEL_EDITOR],
                },
            ],
            horse: {
                id: null,
                name: null,
                importedPhoto: null,
            },
            systemRole: SystemRole.getUserRole(),
            editHorseHeader: null,
            loaded: false,
            showErrorMessages: false,
            validationErrors: [],
            validateRequest: false,
            pendingSave: false,
            deleteDialogStatus: false,
        };
    },

    beforeMount() {
        this.getHorse();
    },

    methods: {
        getHorse() {
            if (this.$route.params.id) {
                getHorseEdit({ id: this.$route.params.id }).then((response) => {
                    this.horse = response.data;
                    this.editHorseHeader = this.horse.name;
                    this.loaded = true;
                });
            } else {
                this.loaded = true;
            }
        },

        isSelected(menuItem) {
            return menuItem.to === this.$route.name;
        },

        // Step 1: Trigger validation in child component
        requestValidationAndSave() {
            this.showErrorMessages = true;
            this.pendingSave = true;
            // Toggle validateRequest to trigger the watcher in the child component
            this.validateRequest = !this.validateRequest;
        },

        // Step 2: Handle the validation result from child
        handleValidationResult(result) {
            if (!this.pendingSave) return;

            this.pendingSave = false;

            if (result.valid) {
                this.handleSave(this.horse);
            } else {
                window.console.error("errors", result.errors);
                this.validationErrors = result.errors;
                this.$toast.add({
                    severity: "error",
                    summary: this.$t("message.other.error"),
                    detail: this.$t("message.other.formError"),
                    life: 5000,
                });
            }
        },

        // Process the save after successful validation
        handleSave(data) {
            editHorse({ body: data }).then(() => {
                this.editHorseHeader = data.name;
                this.$toast.add({
                    severity: "success",
                    summary: this.$t("message.other.success"),
                    detail: this.$t("message.other.dataSaved"),
                    life: 3000,
                });

                this.uploadPhoto(data);
                this.showErrorMessages = false;
                this.validationErrors = [];
            }).catch((err) => {
                window.console.error("errors", err);
                this.notifyError(err);
            });
        },

        notifyError(error) {
            if (error.response?.data) {
                for (const [key, value] of Object.entries(error.response.data)) {
                    if (key !== "path" && key !== "status" && key !== "timestamp") {
                        this.$toast.add({
                            severity: "error",
                            summary: this.$t("message.other.error"),
                            detail: value,
                            life: 5000,
                        });
                    }
                }
            } else {
                window.console.error("errors", error.response);
                this.$toast.add({
                    severity: "error",
                    summary: this.$t("message.other.error"),
                    detail: this.$t("message.other.formError"),
                    life: 5000,
                });
            }
        },

        uploadPhoto(data) {
            if (data.importedPhoto) {
                this.axios.post(
                    `${process.env.VUE_APP_API_URL}/files/photos/${data.id}`,
                    this.horse.importedPhoto,
                    {
                        headers: {
                            "Content-Type": "multipart/form-data",
                        },
                    },
                ).then((response) => {
                    if (response.data && response.data.length > 0) {
                        setHorseAvatar({
                            horseId: data.id,
                            photoId: response.data[0],
                        });
                    }
                }).catch((error) => {
                    console.error("Photo upload error:", error);
                });
            }
        },

        confirmDeleteHorse() {
            // this.confirm.require({
            //     message: this.$t('message.other.deleteConfirmation'),
            //     header: this.$t('message.other.confirmation'),
            //     icon: 'pi pi-exclamation-triangle',
            //     accept: this.deleteHorse,
            //     reject: () => {}
            // });
        },

        deleteHorse() {
            deleteHorse({ id: this.horse.id }).then(() => {
                this.$toast.add({
                    severity: "success",
                    summary: this.$t("message.other.success"),
                    detail: this.$t("message.other.elementDeleted"),
                    life: 3000,
                });
                this.$router.push({path: "/"});
            }).catch((error) => {
                const message = error.response?.data?.message || this.$t("unknownError");
                this.$toast.add({
                    severity: "error",
                    summary: this.$t("message.other.error"),
                    detail: message,
                    life: 5000,
                });
            });
        },

        updateHorseData(value) {
            this.horse = { ...this.horse, ...value };
        },
        hideDeleteDialog() {
            this.deleteDialogStatus = false;
        },
        showDeleteDialog() {
            this.deleteDialogStatus = true;
        },
    },

    computed: {
        userRole() {
            return SystemRole.getUserRole();
        },
        filteredMenuItems() {
            return this.menuItems.filter((item) => {
                // If no roles array is defined, display the item
                if (!item.roles || !Array.isArray(item.roles)) {
                    return true;
                }
                // If roles array exists, check if user's role is in the array
                return item.roles.includes(this.userRole);
            });
        },
        canDeleteHorse() {
            return this.horse && HorseUtils.canDeleteHorse(this.horse.status, SystemRole.getUserRole());
        },
        disableEdit() {
            return this.systemRole !== SystemRole.THIRD_LEVEL_EDITOR && this.systemRole !== SystemRole.ADMIN;
        },
    },

    watch: {
        "$route.params.id": function () {
            this.getHorse();
            this.showErrorMessages = false;
            this.validationErrors = [];
        },
    },
};
</script>

<style lang="scss" scoped>
@import "../../../assets/theme/mytheme/variables";
.horse-edit-layout {
    height: calc(100vh - 40px - 2rem - $top-bar-height);
    .menu {
        font-family: var(--font-family);
        background: var(--background-color);
        min-width: 150px;
        margin-top: 2rem;

        .menu-item {
            text-transform: uppercase;
            font-size: 1.1rem;
            font-weight: 500;
            a {
                color: var(--text-color);
                display: flex;
                align-items: center;
                cursor: pointer;
                padding: 1rem;
            }
            a:hover {
                font-weight: 700;
            }
        }

        .selected-menu-item {
            font-weight: 700;
        }
    }
    .content {
        width: 100%;
        margin: 0;
        height: calc(100vh - 40px - $top-bar-height);
        overflow-y: auto;
    }
    @media screen and (max-width: 1200px) {
        height: unset;
        flex-direction: column;
        .menu {
            align-items: center;
            .menu-buttons {
                width: unset !important;
            }
        }
    }
}
</style>
